import React, { useEffect, useRef, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useAuth } from '@/shared/contexts/AuthContext';
import { usePreviewTransitions } from '@/shared/hooks/usePreviewTransitions';
import { subscribePreviewBlocked } from '@/shared/services/previewGuard';
import {
    PREVIEW_ROLE_LABELS,
    activeRoleName,
} from '@/shared/utils/previewRoles';

/**
 * Shown on every page while a role preview is active. The role named is the
 * one in effect, worded the same way as the label beside the user's name.
 */
export default function PreviewBanner() {
    const { previewRole, effectiveRoles } = useAuth();
    const { exitPreview } = usePreviewTransitions();
    const bannerRef = useRef(null);
    const wasPreviewing = useRef(false);
    const [blockedMessage, setBlockedMessage] = useState('');

    useEffect(() => {
        let timer;
        if (previewRole && !wasPreviewing.current) {
            // After the menu closes: it hands focus back to its toggle.
            timer = setTimeout(() => bannerRef.current?.focus(), 0);
        }
        wasPreviewing.current = Boolean(previewRole);
        if (!previewRole) setBlockedMessage('');
        return () => clearTimeout(timer);
    }, [previewRole]);

    useEffect(() => subscribePreviewBlocked(setBlockedMessage), []);

    if (!previewRole) return null;

    const handleReturn = () => {
        exitPreview();
        document.getElementById('App-main')?.focus();
    };

    return (
        <div
            ref={bannerRef}
            role="status"
            tabIndex={-1}
            aria-label={`Previewing as ${activeRoleName(effectiveRoles)}`}
            className="bg-dark text-white border-bottom border-warning border-4 py-2"
        >
            <Container className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <p className="mb-0">
                    <strong>
                        You are viewing the site as{' '}
                        {PREVIEW_ROLE_LABELS[previewRole]}.
                    </strong>{' '}
                    Nothing you do here changes your account.
                </p>
                <Button
                    variant="light"
                    size="sm"
                    onClick={handleReturn}
                    aria-label="Return to my view"
                >
                    Return to my view
                </Button>
            </Container>
            {blockedMessage && (
                <Container>
                    <p role="alert" className="mb-0 mt-2 text-warning">
                        {blockedMessage}
                    </p>
                </Container>
            )}
        </div>
    );
}
